import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import RolesRedesign from './RolesRedesign';

const ROLES_DEFINITION = [
  { role: 'owner', color: 'var(--accent)', bg: 'var(--surface-2)', border: 'color-mix(in srgb, var(--accent) 20%, transparent)', icon: 'ri-vip-crown-line', description: 'Full control over the tenant. Can manage all settings, users, and data.', permissions: ['Manage tenant settings','Add and remove users','Assign any role','Access all modules','Delete tenant data','Manage billing'] },
  { role: 'admin', color: '#B8860B', bg: 'rgba(184,134,11,0.08)', border: 'rgba(184,134,11,0.2)', icon: 'ri-shield-user-line', description: 'Administrative access. Can manage users and most settings.', permissions: ['Manage users (except owner)','Assign roles up to admin','Access all modules','Manage configurations','View audit logs'] },
  { role: 'editor', color: '#1F5B8F', bg: 'rgba(31,91,143,0.08)', border: 'rgba(31,91,143,0.2)', icon: 'ri-edit-2-line', description: 'Can create and edit records across all modules.', permissions: ['Create and edit counterparties','Create and edit assets','Manage contracts','Upload documents','Submit MMS reports'] },
  { role: 'operator', color: '#0D7A3C', bg: 'rgba(13,122,60,0.08)', border: 'rgba(13,122,60,0.2)', icon: 'ri-settings-4-line', description: 'Operational access. Can manage assets and dispatch tasks.', permissions: ['Manage assets','Submit dispatch tasks','View forecasts','Read-only on contracts','Read-only on financials'] },
  { role: 'finance', color: '#4A4A50', bg: 'rgba(107,107,112,0.08)', border: 'rgba(107,107,112,0.2)', icon: 'ri-money-dollar-circle-line', description: 'Access to financial data, billing, and contracts.', permissions: ['View and export financial reports','Manage contracts','View billing','Read-only on assets','Read-only on forecasts'] },
  { role: 'viewer', color: '#6B6B70', bg: 'rgba(107,107,112,0.05)', border: 'rgba(107,107,112,0.15)', icon: 'ri-eye-line', description: 'Read-only access to all data.', permissions: ['View counterparties','View assets','View contracts','View documents','View forecasts'] },
];

/**
 * Dual mode (handoff section 6): the stand with VITE_REDESIGN shows the
 * redesign, prod keeps RolesPageLegacy below. /admin/roles has no feature
 * gate, so prod does render this route.
 */
export default function RolesPage() {
  return import.meta.env.VITE_REDESIGN === '1' ? <RolesRedesign /> : <RolesPageLegacy />;
}

function RolesPageLegacy() {
  const { isDark } = useTheme();
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';

  return (
    <DashboardLayout breadcrumb={['Administration', 'Roles']}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>Roles</h1>
        <p className="text-sm mt-1" style={{ color: textMuted }}>Platform role definitions and permission matrix</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {ROLES_DEFINITION.map((def) => (
          <div key={def.role} className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${def.border}` }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: def.bg }}><i className={`${def.icon} text-base`} style={{ color: def.color }}></i></div>
              <div><div className="text-sm font-semibold capitalize" style={{ color: def.color }}>{def.role}</div><div className="text-xs" style={{ color: textMuted }}>{def.permissions.length} permissions</div></div>
            </div>
            <p className="text-xs mb-4 leading-relaxed" style={{ color: textMuted }}>{def.description}</p>
            <div className="space-y-1.5">
              {def.permissions.map((perm) => (
                <div key={perm} className="flex items-center gap-2 text-xs" style={{ color: textMain }}>
                  <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0"><i className="ri-check-line text-xs" style={{ color: def.color }}></i></div>{perm}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: textMuted }}>Role Hierarchy</div>
        <div className="flex items-center gap-2 flex-wrap">
          {ROLES_DEFINITION.map((def, idx) => (
            <div key={def.role} className="flex items-center gap-2">
              <span className="text-xs font-medium px-3 py-1.5 rounded-full capitalize" style={{ background: def.bg, color: def.color, border: `1px solid ${def.border}` }}>{def.role}</span>
              {idx < ROLES_DEFINITION.length - 1 && <i className="ri-arrow-right-line text-xs" style={{ color: '#C0C0C8' }}></i>}
            </div>
          ))}
        </div>
        <p className="text-xs mt-3" style={{ color: textMuted }}>Roles are ordered from highest to lowest privilege.</p>
      </div>
    </DashboardLayout>
  );
}